import { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { AnimatePresence, motion } from 'framer-motion';
import { useQuery } from 'react-query';

import { Box, Flex, Heading } from '@chakra-ui/react';
import { useDebouncedState } from '@react-hookz/web';
import { SearchIcon } from '@udacity/chakra-uds-icons';
import { useHeaderContext } from '@udacity/udacity-header-v2';

import { Alert } from '~/components/alert';
import { ButtonLink } from '~/components/button-link';
import { ContentContainer } from '~/components/container';
import { trackEvent } from '~/features/analytics/track-event';
import { SEARCH_RESULTS_PATH } from '~/features/catalog/constants';
import { useTrackCatalogSearchCompleted } from '~/features/catalog/hooks/use-track-catalog-search-completed';
import { ListIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useCatalogPath } from '~/hooks/use-catalog-path';

import { CatalogCardRow } from './catalog-card-row';
import { PersonalizedProgramRecommendationsProps } from './personalized-program-recommendations';

const PersonalizedProgramRecommendations = dynamic<PersonalizedProgramRecommendationsProps>(() =>
  import('./personalized-program-recommendations').then((mod) => mod.PersonalizedProgramRecommendations)
);

function TitleHeader({ title, buttonSlot }: { title: string; buttonSlot?: ReactNode }) {
  return (
    <Flex alignItems='center' gap={4} justifyContent='space-between' mb='16px'>
      <Heading
        alignItems='center'
        as='p'
        display='flex'
        flex={1}
        fontFamily='condensed'
        fontSize='0.9375rem'
        fontWeight='500'
        gap={4}
        letterSpacing='.06em'
        lineHeight='1'
        textTransform='uppercase'
        _after={{
          content: '""',
          display: 'block',
          height: '1px',
          backgroundColor: {
            base: 'blue.300',
            '2xl': 'grey.300',
          },
          flex: 1,
        }}
        color={{
          base: 'blue.300',
          '2xl': 'grey.500',
        }}
      >
        <Flex alignItems='center' gap={2}>
          <SearchIcon height='14px' width='14px' />
          <Box as='span' flexShrink={0} noOfLines={1}>
            {title}
          </Box>
        </Flex>
      </Heading>
      {buttonSlot}
    </Flex>
  );
}

export function SearchResults() {
  const searchValue = useHeaderContext((s) => s.searchValue);
  const isSearchOpen = useHeaderContext((s) => s.isSearchOpen);
  const isSearchPreviewRendered = useHeaderContext((s) => s.isSearchPreviewRendered);
  const [debouncedSearchValue, setDebouncedSearchValue] = useDebouncedState(searchValue, 500, 2000);
  const router = useRouter();
  const companySlug = router.query.companySlug ? (router.query.companySlug as string) : undefined;
  const searchResultsPath = useCatalogPath(SEARCH_RESULTS_PATH);

  setDebouncedSearchValue(searchValue);

  const {
    data: searchResults,
    isLoading: searchResultsIsLoading,
    isFetched: searchResultsIsFetched,
    isFetching: searchResultsIsFetching,
  } = useQuery(
    ['search', debouncedSearchValue],
    async () => {
      const { getSearchResults } = await import('../controllers/get-search-results');
      const results = await getSearchResults(debouncedSearchValue, companySlug);
      try {
        trackEvent({
          type: 'search',
          name: 'Search Bar Clicked',
          label: 'Search',
          action: 'key_words_searched',
          value: debouncedSearchValue,
          additionalAttributes: {
            search_results: results?.searchResultItems?.length ? 'results' : 'no_results_found',
          },
        });
      } catch {
        // Analytics must not break search functionality
      }
      return results;
    },
    {
      enabled: Boolean(debouncedSearchValue),
    }
  );

  useTrackCatalogSearchCompleted({
    searchTerm: debouncedSearchValue,
    resultCount: searchResults?.searchResultItems ? searchResults.numberOfHits : undefined,
    searchSurface: 'header_dropdown',
    isSettled: searchResultsIsFetched && !searchResultsIsFetching && debouncedSearchValue === searchValue,
    isVisible: isSearchOpen && isSearchPreviewRendered,
  });

  return (
    <ContentContainer
      overflow={{ base: 'auto', md: 'initial' }}
      p={{
        base: '28px 0px 40px',
        md: '24px 0px 32px',
      }}
    >
      <AnimatePresence mode='popLayout'>
        {!searchResultsIsFetched && !searchResultsIsLoading && (
          <Box
            key='search-recommended'
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <PersonalizedProgramRecommendations titleSlot={<TitleHeader title='Recommended' />} />
          </Box>
        )}

        {searchResults && Boolean(searchResults.searchResultItems?.length) && !searchResultsIsLoading && (
          <Box
            key='search-results'
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <CatalogCardRow
              catalogItems={searchResults.searchResultItems}
              titleSlot={
                <TitleHeader
                  title={`Results for "${searchValue}"`}
                  buttonSlot={
                    <Box display={{ base: 'none', md: 'block' }} mt='8px'>
                      <ButtonLink
                        linkProps={{ href: `${searchResultsPath}&searchValue=${searchValue}` }}
                        buttonProps={{
                          size: 'icon-left',
                          leftIcon: <ListIcon h='14px' w='14px' />,
                          width: { base: '100%', md: 'fit-content' },
                        }}
                      >
                        All Results
                      </ButtonLink>
                    </Box>
                  }
                />
              }
            />

            <Box display={{ base: 'block', md: 'none' }} mt='24px'>
              <ButtonLink
                linkProps={{ href: `${searchResultsPath}&searchValue=${debouncedSearchValue}` }}
                buttonProps={{
                  leftIcon: <ListIcon h='24px' w='24px' />,
                  size: 'icon-right',
                  width: { base: '100%', md: 'fit-content' },
                  overflow: 'hidden',
                }}
              >
                All Results
              </ButtonLink>
            </Box>
          </Box>
        )}

        {searchResultsIsLoading && (
          <Box
            key='search-skeletons'
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <CatalogCardRow
              isLoading={searchResultsIsLoading}
              titleSlot={<TitleHeader title={`Results for "${debouncedSearchValue}"`} />}
            />
          </Box>
        )}

        {!searchResultsIsLoading && searchResultsIsFetched && !searchResults?.searchResultItems?.length && (
          <Box
            key='search-no-results'
            animate={{ opacity: 1 }}
            as={motion.div}
            exit={{ opacity: 0 }}
            flex={1}
            initial={{ opacity: 0 }}
            width='100%'
          >
            <Alert color='black' status='info'>
              No Results Found
            </Alert>
            <Box h='40px' />
            <PersonalizedProgramRecommendations titleSlot={<TitleHeader title='Popular' />} />
          </Box>
        )}
      </AnimatePresence>
    </ContentContainer>
  );
}
